import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAudience } from '../context/AudienceContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  FileText,
  Heart,
  Brain,
  Zap,
  Activity,
  ArrowRight,
  Clock,
  AlertTriangle,
  Wind,
  Thermometer,
  Utensils,
  Pill,
  ShieldAlert,
  Baby,
  Bone
} from 'lucide-react';

import { API_URL } from '../config';

const protocolIcons = {
  'ACR': Heart,
  'AVC': Brain,
  'EME': Zap,
  'CHOC': Activity,
  'RESPI': Wind,
  'FIEVRE': Thermometer,
  'DIGESTIF': Utensils,
  'DOULEUR': Pill,
  'METABOLIQUE': Activity,
  'INFECTIEUX': ShieldAlert,
  'TRAUMA': AlertTriangle,
  'NEONAT': Baby,
};

const protocolColors = {
  'ACR': { color: 'text-red-400', bg: 'bg-red-400/10', border: 'border-red-400/30' },
  'AVC': { color: 'text-purple-400', bg: 'bg-purple-400/10', border: 'border-purple-400/30' },
  'EME': { color: 'text-yellow-400', bg: 'bg-yellow-400/10', border: 'border-yellow-400/30' },
  'CHOC': { color: 'text-orange-400', bg: 'bg-orange-400/10', border: 'border-orange-400/30' },
  'RESPI': { color: 'text-cyan-400', bg: 'bg-cyan-400/10', border: 'border-cyan-400/30' },
  'FIEVRE': { color: 'text-red-300', bg: 'bg-red-300/10', border: 'border-red-300/30' },
  'DIGESTIF': { color: 'text-green-400', bg: 'bg-green-400/10', border: 'border-green-400/30' },
  'DOULEUR': { color: 'text-pink-400', bg: 'bg-pink-400/10', border: 'border-pink-400/30' },
  'METABOLIQUE': { color: 'text-blue-400', bg: 'bg-blue-400/10', border: 'border-blue-400/30' },
  'INFECTIEUX': { color: 'text-amber-400', bg: 'bg-amber-400/10', border: 'border-amber-400/30' },
  'TRAUMA': { color: 'text-rose-400', bg: 'bg-rose-400/10', border: 'border-rose-400/30' },
  'NEONAT': { color: 'text-violet-400', bg: 'bg-violet-400/10', border: 'border-violet-400/30' },
};

export default function ProtocolsPage() {
  const { token } = useAuth();
  const { audience, isAdult } = useAudience();
  const [searchParams, setSearchParams] = useSearchParams();
  const [protocols, setProtocols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState(searchParams.get('type') || '');

  useEffect(() => {
    fetchProtocols();
  }, [audience, filterType]);

  const fetchProtocols = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/protocols?`;
      if (filterType) url += `type=${filterType}&`;
      
      // Remove trailing ? or & from URL
      url = url.replace(/[?&]$/, '');
      
      const response = await fetch(url);
      
      if (response.ok) {
        const data = await response.json();
        // Filter by audience
        const filtered = data.protocols?.filter(p => p.audience === audience) || [];
        setProtocols(filtered);
      } else {
        console.error('Failed to fetch protocols:', response.status);
        setProtocols([]);
      }
    } catch (error) {
      console.error('Error fetching protocols:', error);
      setProtocols([]);
    } finally {
      setLoading(false);
    }
  };

  // Different types based on audience
  const adultTypes = ['ACR', 'AVC', 'EME', 'CHOC', 'TRAUMA'];
  const pediatricTypes = ['ACR', 'EME', 'CHOC', 'RESPI', 'FIEVRE', 'DIGESTIF', 'DOULEUR', 'METABOLIQUE', 'INFECTIEUX'];
  const types = isAdult ? adultTypes : pediatricTypes;

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <FileText className="w-6 h-6 text-cyan-400" />
          Protocoles d'urgence
        </h1>
        <p className="text-[hsl(var(--text-dim))]">Guides pas-à-pas {isAdult ? 'adulte' : 'pédiatrique'}</p>
      </div>

      {/* Quick Access Links */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Link
          to="/urg-rythmo"
          className="group p-4 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--bg-elev))] hover:bg-red-400/10 hover:border-red-400/30 transition-all hover-lift"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-400/10 group-hover:bg-red-400/20 transition-colors">
              <Heart className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <h3 className="font-semibold text-red-400">ACR</h3>
              <p className="text-sm text-[hsl(var(--text-dim))]">Arrêt Cardio-Respiratoire</p>
            </div>
          </div>
        </Link>

        <Link
          to="/avc-expert"
          className="group p-4 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--bg-elev))] hover:bg-purple-400/10 hover:border-purple-400/30 transition-all hover-lift"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-400/10 group-hover:bg-purple-400/20 transition-colors">
              <Brain className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h3 className="font-semibold text-purple-400">AVC</h3>
              <p className="text-sm text-[hsl(var(--text-dim))]">Accident Vasculaire Cérébral</p>
            </div>
          </div>
        </Link>

        <Link
          to="/neuro-ref"
          className="group p-4 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--bg-elev))] hover:bg-yellow-400/10 hover:border-yellow-400/30 transition-all hover-lift"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-yellow-400/10 group-hover:bg-yellow-400/20 transition-colors">
              <Zap className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <h3 className="font-semibold text-yellow-400">EME</h3>
              <p className="text-sm text-[hsl(var(--text-dim))]">État de Mal Épileptique</p>
            </div>
          </div>
        </Link>

        <Link
          to="/cardio-ref"
          className="group p-4 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--bg-elev))] hover:bg-orange-400/10 hover:border-orange-400/30 transition-all hover-lift"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-orange-400/10 group-hover:bg-orange-400/20 transition-colors">
              <Activity className="w-5 h-5 text-orange-400" />
            </div>
            <div>
              <h3 className="font-semibold text-orange-400">CHOC</h3>
              <p className="text-sm text-[hsl(var(--text-dim))]">Choc cardiogénique & hémorragique</p>
            </div>
          </div>
        </Link>

        <Link
          to="/trauma-ref"
          className="group p-4 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--bg-elev))] hover:bg-rose-400/10 hover:border-rose-400/30 transition-all hover-lift"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-rose-400/10 group-hover:bg-rose-400/20 transition-colors">
              <AlertTriangle className="w-5 h-5 text-rose-400" />
            </div>
            <div>
              <h3 className="font-semibold text-rose-400">TRAUMA</h3>
              <p className="text-sm text-[hsl(var(--text-dim))]">Traumatismes & urgences</p>
            </div>
          </div>
        </Link>

        <Link
          to="/urg-rythmo"
          className="group p-4 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--bg-elev))] hover:bg-cyan-400/10 hover:border-cyan-400/30 transition-all hover-lift"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-cyan-400/10 group-hover:bg-cyan-400/20 transition-colors">
              <Activity className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <h3 className="font-semibold text-cyan-400">URG'RYTHMOLOGIE</h3>
              <p className="text-sm text-[hsl(var(--text-dim))]">Troubles du rythme & conduction</p>
            </div>
          </div>
        </Link>
      </div>

      {/* Type Filters */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant={!filterType ? 'default' : 'outline'}
          size="sm"
          onClick={() => {
            setFilterType('');
            setSearchParams({});
          }}
          className={!filterType ? 'bg-cyan-400/20 text-cyan-300 border-cyan-400/30' : ''}
        >
          Tous
        </Button>
        {types.map((type) => {
          const Icon = protocolIcons[type] || FileText;
          const colors = protocolColors[type] || { color: 'text-cyan-400', bg: 'bg-cyan-400/10' };
          return (
            <Button
              key={type}
              variant={filterType === type ? 'default' : 'outline'}
              size="sm"
              onClick={() => {
                setFilterType(type);
                setSearchParams({ type });
              }}
              className={filterType === type ? `${colors.bg} ${colors.color} ${colors.border}` : ''}
            >
              <Icon className="w-4 h-4 mr-1" />
              {type}
            </Button>
          );
        })}
      </div>

      {/* Protocols List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="skeleton h-32 rounded-xl"></div>
          ))}
        </div>
      ) : protocols.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="w-12 h-12 text-[hsl(var(--muted))] mx-auto mb-4" />
          <p className="text-[hsl(var(--text-dim))]">Aucun protocole trouvé</p>
        </div>
      ) : (
        <div className="space-y-4">
          {protocols.map((protocol) => {
            const Icon = protocolIcons[protocol.type] || FileText;
            const colors = protocolColors[protocol.type] || { color: 'text-cyan-400', bg: 'bg-cyan-400/10', border: 'border-cyan-400/30' };
            
            return (
              <Link
                key={protocol._id}
                to={`/protocols/${protocol._id}`}
                className="protocol-card hover-lift block animate-fade-in"
                data-testid="protocol-item"
              >
                <div className={`protocol-header ${colors.bg} ${colors.border}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg ${colors.bg} flex items-center justify-center`}>
                        <Icon className={`w-5 h-5 ${colors.color}`} />
                      </div>
                      <div>
                        <h3 className="font-semibold">{protocol.title}</h3>
                        <p className="text-sm text-[hsl(var(--text-dim))]">{protocol.description}</p>
                      </div>
                    </div>
                    <ArrowRight className="w-5 h-5 text-[hsl(var(--muted))]" />
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex flex-wrap gap-2 mb-3">
                    <Badge className={`${colors.bg} ${colors.color} ${colors.border}`}>
                      {protocol.type}
                    </Badge>
                    <Badge variant="outline" className="text-[hsl(var(--text-dim))]">
                      <Clock className="w-3 h-3 mr-1" />
                      {protocol.steps?.length || 0} étapes
                    </Badge>
                  </div>
                  {protocol.warnings?.length > 0 && (
                    <div className="flex items-center gap-2 text-sm text-amber-400">
                      <AlertTriangle className="w-4 h-4" />
                      <span>{protocol.warnings[0]}</span>
                    </div>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
